import React from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';

/**
 * Props do FiltersButton
 */
export interface FiltersButtonProps {
  /** Abre/fecha o popover de filtros */
  onClick: () => void;
  /** Quantidade de filtros ativos — badge roxo inline aparece quando > 0 */
  count?: number;
  /** Popover aberto — mantém o fundo ghost-hover */
  active?: boolean;
  /** Classe extra no botão (pass-through). */
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Botão "Filtros" canônico do toolbar (F05): ícone funil 16px + rótulo
 * colapsável + badge de contagem INLINE roxo (pill). Única fonte-da-verdade —
 * as variantes Tickets (sliders/âmbar), Marketing (FiFilter) e Chat migram pra cá.
 *
 * Rótulo (padrão do DS 2026-08-04): `hidden 2xl:@tb:inline` — visível só em
 * viewport ≥2xl (1536px) E toolbar-container ≥ token `tb` (68rem). Requer
 * ancestral `@container` (PageToolbar aplica); sem container a query avalia
 * FALSE e o botão fica só-ícone (desejado em painéis estreitos, ex. Chat).
 *
 * @example
 * ```tsx
 * <FiltersButton
 *   count={activeFilterCount}
 *   active={filtersOpen}
 *   onClick={() => setFiltersOpen((v) => !v)}
 *   data-testid="users-filters"
 * />
 * ```
 */
export const FiltersButton: React.FC<FiltersButtonProps> = ({
  onClick,
  count = 0,
  active = false,
  className,
  disabled = false,
  'data-testid': dataTestId = 'filters-button',
}) => {
  const restingBg = active ? 'var(--theme-button-ghost-hover)' : 'var(--theme-card-background)';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Filtros"
      className={clsx(
        'flex items-center gap-1.5 h-[38px] px-2.5 2xl:@tb:px-3 rounded-lg text-sm font-medium transition-colors flex-none',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      style={{
        border: '1px solid var(--theme-input-border)',
        backgroundColor: restingBg,
        color: 'var(--theme-card-text)',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = restingBg;
      }}
      data-testid={dataTestId}
    >
      <Icon name="filter" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
      <span className="hidden 2xl:@tb:inline">Filtros</span>
      {count > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white"
          style={{ backgroundColor: 'var(--theme-primary)' }}
          data-testid={`${dataTestId}-count`}
        >
          {count}
        </span>
      )}
    </button>
  );
};
